import { chaveDaFonte, rotuloDaTabela } from '@modules/aawz-intelligence-v2/constants/Fontes'

// [T316] Byte a byte o `TIPO_ANEXO` do produtor
// (`aawz_intelligence_v2/core/proveniencia_do_anexo.py:58`). É o único valor
// de `tipo` que o backend emite hoje; um `tipo` desconhecido cai no caminho
// de tabela, que é o comportamento de sempre.
const TIPO_ANEXO = 'anexo'

// Espaços de chave separados: o `tabela` de um anexo é um nome de arquivo e
// pode ser igual ao nome de uma tabela real.
const PREFIXO_ANEXO_CHECKSUM = 'anexo:checksum:'
const PREFIXO_ANEXO_UPLOAD = 'anexo:upload:'
const PREFIXO_ANEXO_NOME = 'anexo:nome:'

/**
 * Agrupa as fontes de um turno por tabela.
 *
 * O backend NÃO consolida: `usecase_ia_crm_chat.py:795` faz
 * `all_sources.extend(result['sources'])` a cada tool executada, então uma
 * tabela consultada por cinco tools chega cinco vezes — várias com
 * `registros: 0` (há inclusive um par hardcoded em
 * `tools/_mover_tarefas_listas.py:206`). Um turno com muitas tools produzia
 * literalmente uma pílula por CHAMADA na tela.
 *
 * A consolidação é de EXIBIÇÃO: o dado persistido continua sendo a lista crua,
 * e mudar isso mexeria em conversa já gravada.
 *
 * A chave de consolidação é `chaveDaFonte`: a tabela em si, ou `bi:<painel>`
 * para a família do BI (o backend manda `bi:<painel>/<componente-ou-uuid>`;
 * dois componentes do mesmo painel viram uma linha, e o uuid não aparece).
 *
 * ★ [T316] ANEXO é a exceção, e a razão é o que a chave significa.
 *
 * O backend publica a proveniência do anexo desde a spec-003 US-3
 * (`core/proveniencia_do_anexo.py::fonte_do_anexo`), e o `tabela` dela é o
 * NOME DO ARQUIVO — posto ali, nas palavras do próprio produtor, "para
 * compatibilidade com o chip de hoje". Consolidar por esse campo funde dois
 * ARQUIVOS DIFERENTES de mesmo nome: dois `extrato.pdf` no mesmo turno (o de
 * janeiro e o de fevereiro) viravam uma pílula com os registros somados, e a
 * tela dizia que a IA leu um arquivo só.
 *
 * Para anexo, a identidade é o CONTEÚDO, e é isso que `chaveDoAnexo` usa —
 * `checksum` primeiro (o hash dos bytes que a tool de fato leu), `upload_id`
 * depois (a tool que não abriu o arquivo não tem hash, e inventá-lo seria
 * proveniência falsa), e o nome só quando não há nenhum dos dois.
 *
 * Ordem: mais registros primeiro; empate pelo rótulo. O desempate não é
 * enfeite — sem ele a mesma resposta reabre com as linhas trocadas de lugar,
 * porque `Array.prototype.sort` não garante estabilidade entre motores para
 * chaves iguais.
 *
 * @param {Array} sources Lista crua, nos três formatos que o banco já tem
 *   (ver `nomeDaFonte`): `{ tabela, registros }`, `{ tabela_ou_api,
 *   registros }` ou a string com o nome da tabela — mais
 *   `{ tipo, anexo, checksum, upload_id }` nas fontes de anexo.
 * @returns {Array} `[{ tabela, rotulo, registros }]`, onde `tabela` é a chave
 *   consolidada, acrescido de `tipo`/`anexo`/`checksum`/`upload_id` QUANDO a
 *   fonte os traz — nunca como `undefined`, para que a linha de uma tabela
 *   comum continue sendo exatamente o que sempre foi.
 */
/**
 * O nome da tabela de um item de `sources` — nos TRÊS formatos que estão
 * gravados no banco (medido em 17/09/2026 sobre
 * `aawz_intelligence_v2_mensagens`, todas as mensagens com `sources`):
 *
 *   `{ tabela, registros }`          1.242 mensagens   26/07 → 17/09
 *   `{ tabela_ou_api, registros }`      92 mensagens   02/09 → 17/09
 *   `"nome_da_tabela"` (string)         80 mensagens   26/08 → 16/09
 *
 * `tabela_ou_api` é o campo da dataclass `Fonte` do backend
 * (`core/visoes/leituras_office/*` e as tools de fee fixo); a string vem de
 * emissores que mandam só o nome, sem contagem. Só o primeiro formato era
 * lido: os outros dois caíam fora sem erro nenhum, e uma resposta com 41
 * fontes chegava à tela sem o chip.
 *
 * A tolerância vive AQUI, e não no produtor, porque o que já está gravado não
 * se reescreve — é a mesma razão pela qual a consolidação é de exibição.
 * `tabela` é a chave canônica e vence quando o item traz as duas.
 */
function nomeDaFonte(fonte) {
  if (typeof fonte === 'string') return fonte.trim()
  if (!fonte) return ''
  const nome = fonte.tabela || fonte.tabela_ou_api
  return typeof nome === 'string' ? nome.trim() : ''
}

export function agruparFontes(sources) {
  const lista = Array.isArray(sources) ? sources : []
  const porTabela = {}

  for (const fonte of lista) {
    // Item sem tabela não vira linha: renderizado, apareceria como um avatar
    // vazio ao lado de um número solto, sem dizer de onde o número veio.
    const nome = nomeDaFonte(fonte)
    if (!nome) continue

    const chave = chaveDeConsolidacao(fonte, nome)
    const registros = Number(fonte && fonte.registros) || 0
    if (porTabela[chave]) {
      porTabela[chave].registros += registros
      continue
    }
    porTabela[chave] = montarLinha(fonte, registros, nome)
  }

  return Object.keys(porTabela)
    .map((chave) => porTabela[chave])
    .sort(compararFontes)
}

/** `true` para a fonte de proveniência de anexo (`tipo: 'anexo'`). */
function ehFonteDeAnexo(fonte) {
  return fonte.tipo === TIPO_ANEXO
}

/**
 * A chave de UM anexo — a identidade do conteúdo, em ordem de confiança.
 *
 * O prefixo separa os três espaços entre si e do espaço das tabelas: o
 * `tabela` de um anexo é um nome de arquivo qualquer e pode coincidir com o
 * nome de uma tabela real (`pm_task`), que é outra coisa.
 */
function chaveDoAnexo(fonte, nome) {
  if (fonte.checksum) return PREFIXO_ANEXO_CHECKSUM + fonte.checksum
  if (fonte.upload_id) return PREFIXO_ANEXO_UPLOAD + fonte.upload_id
  // Sem identificador de conteúdo não há como saber se são dois arquivos ou o
  // mesmo citado duas vezes. Agrupar é a escolha conservadora, e é o
  // comportamento de antes da [T316].
  return PREFIXO_ANEXO_NOME + chaveDaFonte(nome)
}

// `nome` vem de `nomeDaFonte`: ja' normalizado dos TRES formatos que o banco
// tem (string, `tabela`, `tabela_ou_api`). Ler `fonte.tabela` aqui dentro
// descartaria os outros dois em silencio.
function chaveDeConsolidacao(fonte, nome) {
  if (ehFonteDeAnexo(fonte)) return chaveDoAnexo(fonte, nome)
  return chaveDaFonte(nome)
}

/**
 * A linha de exibição. Os campos de anexo entram só quando a fonte os traz —
 * um `tipo: undefined` a mais mudaria o contrato de saída da família de
 * tabelas sem que nada na tela explicasse o porquê.
 */
function montarLinha(fonte, registros, nome) {
  const linha = {
    tabela: chaveDaFonte(nome),
    rotulo: rotuloDaTabela(nome),
    registros,
  }
  if (!ehFonteDeAnexo(fonte)) return linha

  linha.tipo = TIPO_ANEXO
  // `anexo` é o nome como o consultor o vê; o backend cai em "arquivo
  // anexado" quando o upload chega sem `filename`.
  if (fonte.anexo) linha.anexo = fonte.anexo
  if (fonte.checksum) linha.checksum = fonte.checksum
  if (fonte.upload_id) linha.upload_id = fonte.upload_id
  return linha
}

function compararFontes(a, b) {
  if (b.registros !== a.registros) return b.registros - a.registros
  return a.rotulo.localeCompare(b.rotulo)
}
